import {IServiceFactory, IServiceProvider} from "../services/service-factory.interface";
import React, {useEffect, useState} from "react";
import {useServices} from "./use-services.hook";

type CommonProperties<T, U> = Extract<keyof T, keyof U>;

type ComponentProps<TProps> = Omit<TProps, keyof IServiceFactory>

/*
type Permutations<T, K = T> = [T] extends [never]
    ? []
    : K extends K
        ? [K, ...Permutations<Exclude<T, K>>]
        : never;
*/


interface IServiceToInject {
    serviceName: string;
    serviceInstance: any;
}

function createServicesToInjectMap(services: IServiceToInject[]): Record<string, any> {
    return services.reduce((accumulator, current) => {
        accumulator[current.serviceName] = current.serviceInstance;
        return accumulator;
    }, {});
}

function getServicesToInject(serviceProvider: IServiceProvider, servicesNames: string[]): Record<string, any> {

    return createServicesToInjectMap(servicesNames.map(name => {
        return {
            serviceName: name,
            serviceInstance: serviceProvider.getServiceByName(name)
        }
    }));
}

function getUnresolvedServices(servicesMap: Record<string, any>): IServiceToInject[] {
    const result: IServiceToInject[] = [];

    Object.keys(servicesMap).forEach(name => {
        const instance = servicesMap[name];
        if(instance instanceof Promise) {
            result.push({
                serviceName: name,
                serviceInstance: instance
            });
        }
    });

    return result;
}

async function resolveServicesToInject(servicesToInject: IServiceToInject[]): Promise<IServiceToInject[]> {
    return await Promise.all(servicesToInject.map(async (srv) => {
         const resolvedServiceInstance = await (srv.serviceInstance as Promise<any>);
         return {
             serviceName: srv.serviceName,
             serviceInstance: resolvedServiceInstance
         }
    }))
}

interface IWithServicesOptions<TProps> {
    renderSkeleton?: (compProps: ComponentProps<TProps>) => React.ReactElement | null
}

export function withServices<TProps>(Component: React.ComponentType<TProps>,
                                     servicesNamesToInject: CommonProperties<TProps, IServiceFactory>[],
                                     options?: IWithServicesOptions<TProps>) {

    const HOC: React.FC<ComponentProps<TProps>> = (componentProps: ComponentProps<TProps>) => {
        const serviceProvider = (useServices() as any) as IServiceProvider;
        const [servicesToInject, setServicesToInject] = useState<any>(getServicesToInject(serviceProvider, servicesNamesToInject));

        const unresolvedServices = getUnresolvedServices(servicesToInject);

        useEffect(() => {
            if(unresolvedServices.length > 0) {
                resolveServicesToInject(unresolvedServices).then(resolvedServices => {
                    setServicesToInject({
                        ...servicesToInject,
                        ...createServicesToInjectMap(resolvedServices)
                    });
                });
            }
        }, [serviceProvider, servicesToInject, unresolvedServices]);

        if (unresolvedServices.length > 0) {
            if(options?.renderSkeleton) {
                return options?.renderSkeleton(componentProps);
            } else {
                return null;
            }
        }

        return (
            <Component {...componentProps} {...servicesToInject}/>
        );
    }

    return HOC;
}
